import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, DownloadSimple, FileText } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { nasApi, fmtSize, fmtDate } from './nasApi';
import NasOpenLocation, { CopyPathButton } from './NasOpenLocation';
import { NAS_I18N } from './nasI18n';

// NAS-specific preview wrapper. Does NOT reuse DocViewer. Inline for PDF/images
// only; other types show metadata + download. Read-only: no replace/rename/
// move/delete/split/merge/stamp.
export default function NasFilePreview({ nodeId, canDownload, onClose }) {
  const [detail, setDetail] = useState(null);
  const [blob, setBlob] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const d = await nasApi.fileDetail(nodeId);
        setDetail(d);
        if (d.previewable) {
          try {
            const { url, mime } = await nasApi.previewBlobUrl(nodeId);
            revoke = url; setBlob({ url, mime });
          } catch (e) {
            setErr(e?.response?.data?.detail || 'プレビューを表示できません');
          }
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || 'ファイル情報を取得できません');
      } finally { setLoading(false); }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [nodeId]);

  const doDownload = async () => {
    try { await nasApi.download(nodeId, detail?.name); }
    catch (e) { toast.error(e?.response?.data?.detail || 'ダウンロードできません'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="nas-file-preview">
      <div className="absolute inset-0 bg-zinc-900/40" onClick={onClose} aria-hidden />
      <div className="relative ml-auto h-full w-full sm:w-[720px] bg-white shadow-xl flex flex-col">
        <header className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200">
          <FileText size={18} weight="duotone" className="text-teal-700 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-zinc-900 truncate">{detail?.name || '読み込み中…'}</p>
            <p className="text-xs text-zinc-400 truncate" title={detail?.full_path || detail?.logical_path}>
              {detail?.full_path || detail?.logical_path}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="閉じる" title="閉じる"
            data-testid="nas-preview-close"
            className="ml-auto h-9 w-9 flex items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700">
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-auto p-4 bg-zinc-50">
          {detail && (
            <div className="mb-3 rounded-lg border border-zinc-200 bg-white p-3"
              data-testid="nas-preview-location">
              <p className="text-[11px] font-semibold text-zinc-500">{NAS_I18N.location_full_path}</p>
              <div className="mt-1 flex items-start gap-1.5">
                <code className="min-w-0 flex-1 break-all font-mono text-[12px] leading-relaxed text-zinc-800"
                  title={detail.full_path} data-testid="nas-preview-full-path">
                  {detail.full_path || detail.logical_path}
                </code>
                <CopyPathButton value={detail.windows_path || detail.full_path}
                  testid="nas-preview-copy-path" />
              </div>
              {detail.windows_path && (
                <p className="mt-1 truncate font-mono text-[11px] text-zinc-400"
                  title={detail.windows_path}>{detail.windows_path}</p>
              )}
              <NasOpenLocation nodeId={nodeId} className="mt-2" />
            </div>
          )}
          {loading ? (
            <p className="text-sm text-zinc-400 py-10 text-center">読み込み中…</p>
          ) : err ? (
            <p className="text-sm text-rose-600 py-10 text-center" data-testid="nas-preview-error">{err}</p>
          ) : blob && detail?.ext === '.pdf' ? (
            <iframe title="preview" src={blob.url} className="w-full h-[70vh] rounded border border-zinc-200 bg-white" />
          ) : blob ? (
            <img src={blob.url} alt={detail?.name} className="max-w-full mx-auto rounded border border-zinc-200 bg-white" />
          ) : (
            <div className="text-center py-10" data-testid="nas-preview-nopreview">
              <FileText size={40} weight="duotone" className="mx-auto text-zinc-300 mb-2" />
              <p className="text-sm text-zinc-500">この形式はプレビューに対応していません。</p>
            </div>
          )}
          {detail && (
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm bg-white rounded-lg border border-zinc-200 p-3">
              <dt className="text-zinc-400">サイズ</dt><dd className="text-zinc-800">{fmtSize(detail.size)}</dd>
              <dt className="text-zinc-400">更新日時</dt><dd className="text-zinc-800">{fmtDate(detail.modified)}</dd>
              <dt className="text-zinc-400">ソース状態</dt><dd className="text-zinc-800">{detail.source_status}</dd>
              <dt className="text-zinc-400">OCR</dt><dd className="text-zinc-500">{detail.ocr_status === 'indexed' ? '取り込み済み' : '未取り込み'}</dd>
              <dt className="text-zinc-400">AI要約</dt><dd className="text-zinc-500">{detail.summary_status === 'ready' ? '生成済み' : '未生成'}</dd>
              <dt className="text-zinc-400">意味検索</dt><dd className="text-zinc-500">{detail.semantic_status === 'indexed' ? '対応済み' : '未対応'}</dd>
            </dl>
          )}
        </div>
        <footer className="px-4 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
          {canDownload && detail && (
            <Button onClick={doDownload} className="h-9 bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="nas-preview-download">
              <DownloadSimple size={15} className="mr-1.5" /> ダウンロード
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}
